import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [banInfo, setBanInfo] = useState(null);
  const [token, setToken] = useState(() => {
    // Загружаем токен из localStorage при инициализации
    return localStorage.getItem('access_token') || null;
  });

  const applyUserData = (data) => {
    if (data && data.authenticated) {
      localStorage.setItem('username', data.username);
      const userToken = localStorage.getItem('access_token') || data.token || data.access_token;
      if (userToken) {
        setToken(userToken);
      }
      setUser({
        username: data.username,
        id: data.user_id,
        email: data.email || '',
        email_verified: !!data.email_verified,
        plan: data.plan || 'free',
        plan_expires_at: data.plan_expires_at || null,
        is_admin: data.is_admin || false,
        admin_level: data.admin_level || 0,
        max_tunnels: data.max_tunnels || 10,
        max_domains: data.max_domains || 5,
        max_firewall_ips: data.max_firewall_ips || 10
      });
      setBanInfo(null);
      return true;
    }
    return false;
  };

  const applyBan = (data) => {
    if (data && data.banned) {
      setBanInfo({
        banned: true,
        reason: data.ban_reason || data.reason || '',
        until: data.banned_until,
        username: data.username
      });
      setUser(null);
      return true;
    }
    return false;
  };

  // Проверяем авторизацию при загрузке через API
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const data = await authAPI.checkAuth();
        if (!applyUserData(data)) {
          applyBan(data);
          if (!data || (!data.banned && !data.authenticated)) {
            setUser(null);
          }
        }
      } catch (err) {
        if (err.response && err.response.status === 403) {
          applyBan(err.response.data);
        }
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Обновляет данные пользователя через /auth/me (после подтверждения email и т.д.)
  const refreshUser = async () => {
    try {
      const data = await authAPI.checkAuth();
      if (applyUserData(data)) {
        return data;
      }
      return null;
    } catch (err) {
      setUser(null);
      return null;
    }
  };

  // Регистрация (требует подтверждения email — автологина нет)
  const register = async (username, email, password, captchaToken) => {
    setError(null);
    try {
      return await authAPI.register(username, email, password, captchaToken);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Логин
  const login = async (username, password) => {
    setError(null);
    setBanInfo(null);
    try {
      const loginData = await authAPI.login(username, password);
      // Сохраняем токен если он в ответе
      if (loginData && (loginData.token || loginData.access_token)) {
        const userToken = loginData.token || loginData.access_token;
        localStorage.setItem('access_token', userToken);
        setToken(userToken);
      }
      const data = await authAPI.checkAuth();
      if (!applyUserData(data)) {
        if (applyBan(data)) {
          throw new Error('Аккаунт заблокирован');
        }
        setUser({ username, is_admin: false, admin_level: 0, max_tunnels: 10 });
      }
      return true;
    } catch (err) {
      // Проверяем если это ответ 403 с информацией о бане
      if (err.response && err.response.status === 403 && err.response.data && err.response.data.banned) {
        applyBan(err.response.data);
        return false; // Не бросаем ошибку, просто показываем модалку
      }
      setError(err.message || err.response?.data?.error || 'Ошибка авторизации');
      throw err;
    }
  };

  // Логаут
  const logout = async () => {
    try {
      await authAPI.logout();
    } finally {
      localStorage.removeItem('username');
      localStorage.removeItem('access_token');
      setUser(null);
      setToken(null);
      setBanInfo(null);
    }
  };

  const value = {
    user,
    loading,
    error,
    banInfo,
    token,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    refreshUser,
    updateUser: (patch) => setUser((prev) => (prev ? { ...prev, ...patch } : prev)),
    clearError: () => setError(null),
    clearBanInfo: () => setBanInfo(null),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
